import {
  AnyCustomFieldDefinition,
  isFieldsetDefinition,
} from '@payaca/custom-fields';

/**
 * `false` and `0` are answers, not blanks. "No defects found" on a Gas Safety
 * Record and "0 faults" on an EICR are the compliance records we most need to
 * keep visible, so only absent values count as unpopulated.
 */
const isPopulatedValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.some(isPopulatedValue);
  if (typeof value === 'object')
    return Object.values(value).some(isPopulatedValue);
  return true;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * How many of a fieldset's fields hold a value, counting the leaves. A nested
 * fieldset contributes each of its own populated fields rather than counting as
 * one, so the number reflects how much has actually been filled in.
 *
 * Which fields are nested fieldsets comes from the definitions, not the shape of
 * the value: a `url` or `user` field also stores an object, so the value alone
 * cannot tell a nested fieldset from an answered leaf.
 */
export const countPopulatedFields = (
  definitions: AnyCustomFieldDefinition[],
  value: unknown
): number =>
  definitions.reduce((count, definition) => {
    const fieldValue = isRecord(value)
      ? value[definition.identifier]
      : undefined;

    if (isFieldsetDefinition(definition)) {
      return count + countPopulatedFields(definition.children, fieldValue);
    }

    return count + (isPopulatedValue(fieldValue) ? 1 : 0);
  }, 0);
